import React, { useMemo } from "react";
import { BarChart } from "@tremor/react";
import { Alert, Card } from "antd";
import dayjs from "dayjs";
import groupBy from "lodash.groupby";

import { getHydrationBreakdown } from "@fitness-app/data-models/entities/HydrationEntry";
import { MeasurementType } from "@fitness-app/data-models/entities/Measurement";

import {
  useHydrationEntriesForRange,
  useMeasurementsForRange,
} from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/hooks/useMeasurementsForRange";
import { type CommonChartProps } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/types";

const valueFormatterAbsolute = (number: number) => `${Intl.NumberFormat("pl").format(number).toString()} ml`;

// TRE-2413: słupki rozbite na napoje. Woda = suma dnia minus pozostałe napoje, więc dni sprzed
// rozbicia (i woda dodana poza arkuszem w apce) pokazują się jako sama woda.
const CATEGORIES = ["Woda", "Kawa", "Herbata", "Inne"];

export const HydrationChart = ({ range }: CommonChartProps): React.ReactElement => {
  const { data: measurements = [], isInitialLoading } = useMeasurementsForRange(
    MeasurementType.HydrationMeasurement,
    range,
  );
  const {
    data: entries = [],
    isInitialLoading: entriesLoading,
    isError: entriesFailed,
  } = useHydrationEntriesForRange(range);

  const chartData = useMemo(() => {
    // Bez wpisów nie wiemy, ile było kawy czy herbaty: pokazujemy samą sumę, nie „samą wodę”.
    if (entriesFailed) {
      return measurements.map((item) => ({
        date: dayjs(item.eventDate).format("DD.MM.YYYY"),
        Razem: item.data.portion,
      }));
    }
    const entriesByDay = groupBy(entries, (entry) => entry.eventDate);
    return measurements.map((item) => {
      const breakdown = getHydrationBreakdown(item.data.portion, entriesByDay[item.eventDate] ?? []);
      return {
        date: dayjs(item.eventDate).format("DD.MM.YYYY"),
        Woda: breakdown.water,
        Kawa: breakdown.coffee,
        Herbata: breakdown.tea,
        Inne: breakdown.other,
      };
    });
  }, [measurements, entries, entriesFailed]);

  return (
    <Card title="Nawodnienie" type="inner" className="relative" loading={isInitialLoading || entriesLoading}>
      {entriesFailed ? (
        <Alert type="warning" showIcon message="Nie udało się pobrać rozbicia na napoje. Wykres pokazuje samą sumę." />
      ) : null}
      <BarChart
        className="mt-8 h-80"
        data={chartData}
        index="date"
        stack
        colors={entriesFailed ? ["blue"] : ["blue", "amber", "emerald", "gray"]}
        categories={entriesFailed ? ["Razem"] : CATEGORIES}
        showLegend={!entriesFailed}
        valueFormatter={valueFormatterAbsolute}
        yAxisWidth={90}
        autoMinValue
        allowDecimals={false}
        intervalType={"preserveStartEnd"}
        noDataText="Brak danych do wyświetlenia"
      />
    </Card>
  );
};
